import React, { useMemo } from 'react';
import { useIntl } from 'umi';
import { Select } from 'tea-component';
import GridBox from '@/pages/datalab/components/GridBox';
import { COLORS_MAP, CUSTOM_HEXS } from './constants';
import CustomColorSelector from '@/pages/datalab/components/CustomColorSelector';
import ColorSvg from '@/pages/datalab/components/CustomColorSelector/ColorSvg';
import type { IColor } from '../ConditionalFormat/type';
import './index.scss';

interface IColorMapEntry {
  value: string;
  nameId: string;
  colors: string[];
}

type TColorMap = Record<string, IColorMapEntry>;

interface IMapColorValue {
  colorName: string;
  colors: IColor[];
}

interface IColorSelectorItemProps {
  name: string;
  colors: string[];
}

const ColorSelectorItem: React.FC<IColorSelectorItemProps> = ({
  name,
  colors,
}) => (
  <div className="dbl-color-selector-item">
    <span className="dbl-color-selector-item-label">{name}</span>
    <span className="dbl-color-selector-item-colors">
      {colors?.map(color => (
        <ColorSvg color={color} key={color} />
      ))}
    </span>
  </div>
);

export interface IColorThemeProps {
  colorName?: string;
  colors?: IColor[];
  onChange: (value: IMapColorValue) => void;
  colorMap?: TColorMap;
  label?: string;
}

const ColorTheme: React.FC<IColorThemeProps> = ({
  colorName = 'blue',
  colors = [],
  onChange,
  colorMap = COLORS_MAP,
  label = 'dbl_chart_map_level',
}) => {
  const { formatMessage, locale } = useIntl();
  const options = useMemo(
    () =>
      Object.values(colorMap).map(item => ({
        value: item.value,
        text: (
          <ColorSelectorItem
            name={formatMessage({ id: item.nameId })}
            colors={item.value === 'custom' ? [] : item.colors}
          />
        ),
      })),
    [locale, colorMap, formatMessage],
  );

  const handleChangeCustomColor = (newColor: IColor, index: number): void => {
    const newColors = [...colors];
    newColors[index] = newColor;
    onChange({
      colorName: 'custom',
      colors: newColors,
    });
  };

  const newColors = colors?.map(color => color?.hexa);
  const name = colorMap?.[colorName]?.nameId
    ? formatMessage({ id: colorMap[colorName].nameId })
    : '';

  return (
    <>
      <div className="dbl-map-color-theme">
        <span className="dbl-map-color-theme-label">
          {formatMessage({ id: 'dbl_chart_map_color_theme' })}
        </span>
        <Select
          className="dbl-map-color-theme-select"
          appearance="button"
          options={options}
          value={colorName}
          listWidth={210}
          listHeight={200}
          onChange={value => {
            if (value === colorName) return;
            onChange({
              colorName: value,
              colors:
                colorMap[value]?.colors?.map(color => ({
                  hexa: color,
                  alpha: '100',
                })) ?? [],
            });
          }}
          button={
            <ColorSelectorItem
              key={newColors?.join('-')}
              name={name}
              colors={newColors}
            />
          }
        />
      </div>
      <GridBox
        className="dbl-map-color-level"
        grid={{ row: 5, column: 2 }}
        columnSizes={[{ value: '47px' }, { value: '202px' }]}
        gap={20}
      >
        {colors?.map((item, index) => (
          <React.Fragment key={`${label}-${index}`}>
            <GridBox.Item type="label">
              {formatMessage({ id: `${label}${index + 1}` })}
            </GridBox.Item>
            <GridBox.Item>
              <CustomColorSelector
                colorInfo={item}
                onChangeColorInfo={colorInfo =>
                  handleChangeCustomColor(colorInfo, index)
                }
                presetColors={CUSTOM_HEXS}
                style={{ width: '202px' }}
              />
            </GridBox.Item>
          </React.Fragment>
        ))}
      </GridBox>
    </>
  );
};

export default ColorTheme;
